/**
 * FUTURE OPTIMIZATION: Client-side audio extraction for files < 2GB
 *
 * A working lamejs implementation exists in branch: experiment/extract-audio-from-video
 * that can extract audio from video files client-side. This could be used
 * as a fast path for smaller files, with the Lambda server-side extraction
 * as a fallback for files >= 2GB.
 *
 * Followed this tutorial: https://dev.to/tiagosilvapereira/converting-audio-to-mp3-in-the-browser-javascript-tutorial-1dcm
 *
 * See: [GitHub Branch](https://github.com/FanWord/fanword-boost/tree/experiment/extract-audio-from-video)
 */

import { Controller } from "@hotwired/stimulus"
import { createAlert } from '../scripts/createAlert';

let search_debounce = undefined;
let update_debounce = undefined;

export default class extends Controller {
  static targets = [ "search", "chunk", "exportButton", "exportSubmitButton", "logoUpload"]
  static values = {
    transcriptionId: String
  }

  clonedAudioElement = undefined;
  playingAudio = false;
  modal = undefined;
  searchResults = [];
  currentSearchResultIndex = 0;

  connect() {
    // console.log("Hello, from transcription stimulus controller!", this.element)
    let modalElement = document.getElementById('transcription-export-modal');
    this.modal = modalElement && new bootstrap.Modal(modalElement);

    this.scrollToChunkFromHash();
  }

  scrollToChunkFromHash() {
    const chunkId = window.location.hash.replace('#', '');
    if (!chunkId.startsWith('transcription-chunk-')) {
      return;
    }

    const chunkElement = document.getElementById(chunkId);
    if (!chunkElement) {
      return;
    }

    chunkElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const chunkTextElement = chunkElement.querySelector('[contenteditable="true"]');
    if (chunkTextElement) {
      chunkTextElement.focus();
    }
  }

  updateSpeaker(event) {
    if (event.key == 'Enter') {
      event.preventDefault();
      return;
    }

    // console.log("Updating speaker", event.target.innerText)

    const speakerId = event.target.dataset.transcriptionSpeakerId;
    const speakerName = event.target.value;

    // Instead of setting innerText (which resets cursor & caret), update only other matching "speaker-name"s
    this.chunkTargets.forEach(chunk => {
      if (chunk.dataset.transcriptionSpeakerId == speakerId) {
        const speakerNameElements = chunk.querySelectorAll('.speaker-name');
        speakerNameElements.forEach(el => {
          // Don't update the currently edited element to avoid resetting caret
          if (el !== event.target) {
            el.value = speakerName;
          }
        });
      }
    });

    if (update_debounce) {
      clearTimeout(update_debounce);
    }

    update_debounce = setTimeout(() => {

      // Update the transcription speaker name in the database
      fetch(`/transcriptions/${this.transcriptionIdValue}/speakers/${speakerId}.json`, {
        method: 'PATCH',
        body: JSON.stringify({ speaker: { name: speakerName } }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      }).then(response => response.json()).then(data => {
        // console.log("Transcription speaker updated", data);
        createAlert('success', 'Transcription speaker updated');
      }).catch(error => {
        // console.error("Error updating transcription speaker", error);
      });

    }, 1000);

  }

  updateRole(event) {
    // console.log("Updating role", event.target.value)
    const role = event.target.value;
    const speakerId = event.target.dataset.transcriptionSpeakerId;
    // debugger;

    this.chunkTargets.forEach(chunk => {
      if (chunk.dataset.transcriptionSpeakerId == speakerId) {
        chunk.querySelector('select').value = role;
      }
    });

    // Update the transcription speaker name in the database
    fetch(`/transcriptions/${this.transcriptionIdValue}/speakers/${speakerId}.json`, {
      method: 'PATCH',
      body: JSON.stringify({ speaker: { title: role } }),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    }).then(response => response.json()).then(data => {
      // console.log("Transcription speaker updated", data);
      createAlert('success', 'Transcription speaker updated');
    }).catch(error => {
      // console.error("Error updating transcription speaker", error);
    });
  }

  updateChunkText(event) {
    // console.log("Updating chunk text", event.target.innerText)

    const chunkText = event.target.innerText;
    const chunkId = event.target.dataset.transcriptionChunkId;

    if (update_debounce) {
      clearTimeout(update_debounce);
    }

    update_debounce = setTimeout(() => {

      // Update the transcription speaker name in the database
      fetch(`/transcriptions/${this.transcriptionIdValue}/chunks/${chunkId}.json`, {
        method: 'PATCH',
        body: JSON.stringify({ chunk: { text: chunkText } }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      }).then(response => response.json()).then(data => {
        // console.log("Transcription speaker updated", data);
        createAlert('success', 'Transcription text updated');
      }).catch(error => {
        // console.error("Error updating transcription speaker", error);
      });

    }, 1000);

  }

  search(event) {
    // console.log("Searching", event.target.value)
    const searchValue = event.target.value

    if (search_debounce) {
      clearTimeout(search_debounce);
    }

    search_debounce = setTimeout(() => {
      this.findAllSearchResults(searchValue);

      this.currentSearchResultIndex = 0;
      this.scrollToSearchResult(this.currentSearchResultIndex);
    }, 500);
  }

  findAllSearchResults(searchValue) {
    this.searchResults = [];

    this.chunkTargets.forEach(chunk => {
      const chunkText = chunk.querySelector('p').textContent
      // Highlight matching searchValue in the chunk, or remove existing highlights if no match
      const p = chunk.querySelector('p');
      const originalText = p.textContent;

      // Utility function to remove all mark spans but keep their content
      function removeMarks(el) {
        const marks = el.querySelectorAll('span.mark');
        marks.forEach(mark => {
          const parent = mark.parentNode;
          parent.replaceChild(document.createTextNode(mark.textContent), mark);
          parent.normalize();
        });
      }

      // Remove any previous highlights
      removeMarks(p);

      if (searchValue && chunkText.toLowerCase().includes(searchValue.toLowerCase())) {
        // Use regex to build highlighted HTML
        // Escape regex characters in searchValue
        const escapedSearchValue = searchValue.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedSearchValue, 'gi');
        p.innerHTML = originalText.replace(regex, match => `<span class="mark" style="background-color: yellow;">${match}</span>`);
        this.searchResults.push(p);
      } else {
        // Restore to plain text (marks are already removed above)
        p.textContent = originalText;
      }
    })
  }

  scrollToSearchResult(index) {
    debugger;
    this.currentSearchResultIndex = index;
    this.searchResults[index].scrollIntoView({ behavior: 'smooth' });
  }

  cycleResults(event) {
    event.preventDefault();

    // do nothing on enter press with no search term
    if (event.target.value == '') {
      return;
    }

    // loop back to beginning if at the end of the results
    this.currentSearchResultIndex++;
    if (this.currentSearchResultIndex >= this.searchResults.length) {
      this.currentSearchResultIndex = 0;
    }

    this.scrollToSearchResult(this.currentSearchResultIndex);
  }

  toggleAudio(event) {
    const start = event.currentTarget.dataset.transcriptionStartParam;
    const end = event.currentTarget.dataset.transcriptionEndParam;

    // Save a reference to the triggering chunk element for button state toggling in handlers
    this._activeChunkElement = event.currentTarget;

    const playButton = this._activeChunkElement.querySelector('#play-button');
    const pauseButton = this._activeChunkElement.querySelector('#pause-button');

    // console.log("Playing audio", this._activeChunkElement, start, end);

    // make a copy of the audio element
    if (!this.clonedAudioElement) {
      this.clonedAudioElement = document.querySelector('audio').cloneNode(true);
    }

    if (this.playingAudio == true) {

      // Pause Audio

      this.clonedAudioElement.pause();
      this.playingAudio = false;

      // show the play button again
      pauseButton.classList.remove('d-inline');
      pauseButton.classList.add('d-none');
      playButton.classList.remove('d-none');
      playButton.classList.add('d-inline');

    } else {

      // Play Audio for a specific segment from start to end

      // Make sure previous event listener is removed to avoid stacking.
      if (this._audioTimeUpdateHandler) {
        this.clonedAudioElement.removeEventListener('timeupdate', this._audioTimeUpdateHandler);
        this._audioTimeUpdateHandler = null;
      }

      if (this._audioEndedHandler) {
        this.clonedAudioElement.removeEventListener('ended', this._audioEndedHandler);
        this._audioEndedHandler = null;
      }

      // Convert start and end to floats
      const startTime = parseFloat(start);
      const endTime = parseFloat(end);

      this.clonedAudioElement.currentTime = startTime;
      this.clonedAudioElement.play();
      this.playingAudio = true;

      pauseButton.classList.remove('d-none');
      pauseButton.classList.add('d-inline');
      playButton.classList.remove('d-inline');
      playButton.classList.add('d-none');

      // Capture a strong reference to the _activeChunkElement for the handler
      this._audioTimeUpdateHandler = (audioEvent) => {
        // Use stored reference to buttons so handler manipulates correct elements
        const chunkElement = this._activeChunkElement;
        const playBtn = chunkElement.querySelector('#play-button');
        const pauseBtn = chunkElement.querySelector('#pause-button');
        // End handling at endTime
        if (this.clonedAudioElement.currentTime >= endTime) {
          this.clonedAudioElement.pause();
          this.playingAudio = false;

          // Reset to show play button
          pauseBtn.classList.remove('d-inline');
          pauseBtn.classList.add('d-none');
          playBtn.classList.remove('d-none');
          playBtn.classList.add('d-inline');

          // Optionally, reset audio to startTime for replay
          this.clonedAudioElement.currentTime = startTime;
        }
      };

      this._audioEndedHandler = () => {
        // Use stored reference to buttons so handler manipulates correct elements
        const chunkElement = this._activeChunkElement;
        const playBtn = chunkElement.querySelector('#play-button');
        const pauseBtn = chunkElement.querySelector('#pause-button');
        pauseBtn.classList.remove('d-inline');
        pauseBtn.classList.add('d-none');
        playBtn.classList.remove('d-none');
        playBtn.classList.add('d-inline');
      };

      this.clonedAudioElement.addEventListener('timeupdate', this._audioTimeUpdateHandler);
      this.clonedAudioElement.addEventListener('ended', this._audioEndedHandler);

    }
  }

  updateExportOption(event) {
    console.log("Updating export option", event.target.value);

    const format = event.target.value;

    if (format == 'text' || format == 'docx' || format == '') {
      document.getElementById('include_logo').disabled = true;
      document.getElementById('include_logo').checked = false;
      this.logoUploadTarget.classList.add('d-none');
    } else {
      document.getElementById('include_logo').disabled = false;

      if (document.getElementById('include_logo').checked) {
        this.logoUploadTarget.classList.remove('d-none');
      } else {
        this.logoUploadTarget.classList.add('d-none');
      }
    }

    if (format) {
      this.exportSubmitButtonTarget.disabled = false;
    } else {
      this.exportSubmitButtonTarget.disabled = true;
    }
  }

  updateLogoUpload(event) {
    if (event.target.checked) {
      this.logoUploadTarget.classList.remove('d-none');
    } else {
      this.logoUploadTarget.classList.add('d-none');
    }
  }

  export(event) {

    event.target.disabled = true;
    event.target.textContent = 'Downloading...';

    document.getElementById('transcription-export-form').submit();

    setTimeout( () => {

      document.getElementById('transcription-export-form').reset();

      event.target.disabled = false;
      event.target.textContent = 'Download';

      this.modal.hide();

      createAlert('success', 'Transcription exported');

    }, 2000);

  }

}
